import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { vetrineService } from '../../services/vetrineService';

import ProductsManager from './components/ProductsManager';
import ServicesManager from './components/ServicesManager';
import PromotionsManager from './components/PromotionsManager';
import CashbackManager from './components/CashbackManager';
import ReviewsManager from './components/ReviewsManager';

const AdminContentManagement = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const storeId = searchParams?.get('storeId');
  
  const [activeTab, setActiveTab] = useState('products');
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (storeId) {
      loadStoreData();
    } else {
      setLoading(false);
    }
  }, [storeId]);

  const loadStoreData = async () => {
    try {
      setLoading(true);
      const storeData = await vetrineService?.getStoreById(storeId);
      setStore(storeData);
    } catch (error) {
      console.error('Error loading store:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'products', label: 'Prodotti', icon: 'Package' },
    { id: 'services', label: 'Servizi', icon: 'Briefcase' },
    { id: 'promotions', label: 'Promozioni', icon: 'Tag' },
    { id: 'cashback', label: 'Cashback', icon: 'DollarSign' },
    { id: 'reviews', label: 'Recensioni', icon: 'Star' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header onFilterClick={() => {}} />
        <div className="pt-24 pb-12 px-6">
          <div className="max-w-screen-2xl mx-auto">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Caricamento...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!storeId || !store) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Gestione Contenuti - CLICCAMI.NET</title>
        </Helmet>
        <Header onFilterClick={() => {}} />
        <div className="pt-24 pb-12 px-6">
          <div className="max-w-screen-2xl mx-auto">
            <div className="text-center py-12">
              <Icon name="AlertCircle" size={48} className="mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-2xl font-bold mb-2">Nessuna vetrina selezionata</h2>
              <p className="text-muted-foreground mb-6">Seleziona una vetrina per gestire i suoi contenuti</p>
              <Button onClick={() => navigate('/admin-vetrine')}>Vai a Gestione Vetrine</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Gestione Contenuti - {store?.nomeNegozio} - CLICCAMI.NET</title>
      </Helmet>
      <Header onFilterClick={() => {}} />
      
      <div className="pt-24 pb-12 px-6">
        <div className="max-w-screen-2xl mx-auto">
          {/* Header Section */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/admin-vetrine')}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-smooth mb-4"
            >
              <Icon name="ArrowLeft" size={20} />
              <span>Torna a Gestione Vetrine</span>
            </button>
            
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  Gestione Contenuti: {store?.nomeNegozio}
                </h1>
                <p className="text-muted-foreground">
                  Gestisci prodotti, servizi, promozioni, cashback e recensioni
                </p>
              </div>
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className="bg-card rounded-lg shadow-warm mb-6">
            <div className="flex overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-6 py-4 font-medium transition-smooth whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={20} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="bg-card rounded-lg shadow-warm p-6">
            {activeTab === 'products' && <ProductsManager storeId={storeId} />}
            {activeTab === 'services' && <ServicesManager storeId={storeId} />}
            {activeTab === 'promotions' && <PromotionsManager storeId={storeId} />}
            {activeTab === 'cashback' && <CashbackManager storeId={storeId} />}
            {activeTab === 'reviews' && <ReviewsManager storeId={storeId} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminContentManagement;